import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { usePageTracking } from './hooks/usePageTracking';
import { useMetaTags } from './hooks/useMetaTags';
import { useTranslation } from 'react-i18next';
import HeroSection from './components/HeroSection';
import StatsSection from './components/StatsSection';
import FeatureCarousel from './components/FeatureCarousel';
import FeatureSections from './components/FeatureSections';
import Reviews from './components/Reviews';
import PricingSection from './components/PricingSection';
import FAQSection from './components/FAQSection';
import Footer from './components/Footer';
import Navigation from './components/Navigation';
import CTABanner from './components/CTABanner';
import PasswordProtected from './components/PasswordProtected';
import LocaleRouter from './components/LocaleRouter';
import DownloadRedirect from './components/DownloadRedirect';
import LocalizedDownloadRedirect from './components/LocalizedDownloadRedirect';
import RedeemRedirect from './components/RedeemRedirect';
import CampaignRedirect from './components/CampaignRedirect';
import { isNavHidden } from './utils/navVisibility';

// Routes the home page never renders are split out of the main bundle: these
// pull in Firestore, recharts and the QR library, none of which should be on
// the critical path to the hero's first paint.
const TermsOfUse = lazy(() => import('./components/TermsOfUse'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const PricingPage = lazy(() => import('./components/Pricing'));
const FeaturesPage = lazy(() => import('./components/Features'));
const ContactPage = lazy(() => import('./components/Contact'));
const AboutPage = lazy(() => import('./components/About'));
const QRPage = lazy(() => import('./components/QRPage'));
const QRDemoPage = lazy(() => import('./components/QRDemoPage'));
const ProcessLogs = lazy(() => import('./components/ProcessLogs'));
const FeedbackDashboard = lazy(() => import('./components/FeedbackDashboard'));
const Subscriptions = lazy(() => import('./components/Subscriptions'));
const DailyUsage = lazy(() => import('./components/DailyUsage'));
const Revenue = lazy(() => import('./components/Revenue'));
const PromoCodes = lazy(() => import('./components/PromoCodes'));
const CampaignLanding = lazy(() => import('./components/CampaignLanding'));



// Home Page
const HomePage = () => (
  <div style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)' }}>
    <HeroSection />
    <StatsSection />
    <FeatureSections />
    <FeatureCarousel />
    <Reviews />
    <PricingSection />
    <CTABanner />
    <FAQSection />
    <Footer />
  </div>
);

// Terms Page
const TermsPage = () => (
  <>
    <TermsOfUse />
    <Footer />
  </>
);

// Privacy Page
const PrivacyPage = () => (
  <>
    <PrivacyPolicy />
    <Footer />
  </>
);

// English-only wrapper components for non-localized routes
const EnglishTermsPage = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage('en');
  }, [i18n]);

  return (
    <>
      <TermsOfUse />
      <Footer />
    </>
  );
};

const EnglishPrivacyPage = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage('en');
  }, [i18n]);

  return (
    <>
      <PrivacyPolicy />
      <Footer />
    </>
  );
};

const PricingFullPage = () => <><PricingPage /><Footer /></>;
const FeaturesFullPage = () => <><FeaturesPage /><Footer /></>;
const ContactFullPage = () => <><ContactPage /><Footer /></>;
const AboutFullPage = () => <><AboutPage /><Footer /></>;



// App Content Component (needs to be inside BrowserRouter to use hooks)
const AppContent = () => {
  usePageTracking();
  useMetaTags();

  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    const timer = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [hash]);

  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)' }}>
      <Navigation />
      <div className={isNavHidden(pathname) ? '' : 'pt-16'}>
      <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
      <Routes>
          {/* Default route redirects to language-specific route */}
          <Route path="/" element={<LocaleRouter><HomePage /></LocaleRouter>} />
          
          {/* Locale-based routes */}
          <Route path="/:locale" element={<LocaleRouter><HomePage /></LocaleRouter>} />
          <Route path="/:locale/" element={<LocaleRouter><HomePage /></LocaleRouter>} />
          <Route path="/:locale/pricing" element={<LocaleRouter><PricingFullPage /></LocaleRouter>} />
          <Route path="/:locale/features" element={<LocaleRouter><FeaturesFullPage /></LocaleRouter>} />
          <Route path="/:locale/contact" element={<LocaleRouter><ContactFullPage /></LocaleRouter>} />
          <Route path="/:locale/about" element={<LocaleRouter><AboutFullPage /></LocaleRouter>} />
          <Route path="/:locale/terms" element={<LocaleRouter><TermsPage /></LocaleRouter>} />
          <Route path="/:locale/privacy" element={<LocaleRouter><PrivacyPage /></LocaleRouter>} />
          <Route path="/:locale/download" element={<LocalizedDownloadRedirect />} />
          
          {/* Non-localized routes (admin/dashboard) */}
          <Route path="/dashboard-metrics" element={<PasswordProtected><Dashboard /></PasswordProtected>} />
          <Route path="/dashboard-process-logs" element={<PasswordProtected><ProcessLogs /></PasswordProtected>} />
          <Route path="/dashboard-logs-feedback" element={<PasswordProtected><FeedbackDashboard /></PasswordProtected>} />
          <Route path="/dashboard-subscriptions" element={<PasswordProtected><Subscriptions /></PasswordProtected>} />
          <Route path="/dashboard-daily-usage" element={<PasswordProtected><DailyUsage /></PasswordProtected>} />
          <Route path="/dashboard-promo-codes" element={<PasswordProtected><PromoCodes /></PasswordProtected>} />
          <Route path="/dashboard-revenue" element={<PasswordProtected><Revenue /></PasswordProtected>} />
          <Route path="/qr" element={<QRPage />} />
          <Route path="/qrcode" element={<QRDemoPage />} />
          <Route path="/:locale/qr" element={<LocaleRouter><QRPage /></LocaleRouter>} />
          <Route path="/:locale/qrcode" element={<LocaleRouter><QRDemoPage /></LocaleRouter>} />
          <Route path="/download" element={<DownloadRedirect />} />
          <Route path="/code/:code" element={<RedeemRedirect />} />
          <Route path="/c/:campaignId" element={<CampaignLanding />} />
          
          {/* Non-localized legal pages (default to English) */}
          <Route path="/privacy" element={<EnglishPrivacyPage />} />
          <Route path="/terms" element={<EnglishTermsPage />} />
        </Routes>
      </Suspense>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;